import React, { useState, useEffect } from "react";
import { db, auth } from "./firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { collection, addDoc, Timestamp, doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./Edit.css";

const QuizCreation = () => {
    // eslint-disable-next-line
    const [user, setUser] = useState(null);
    // eslint-disable-next-line
    const [profile, setProfile] = useState(null)
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [quizType, setQuizType] = useState("standard");
    const [questions, setQuestions] = useState([]);
    const [flashcards, setFlashcards] = useState([]);
    const [ispublic, setIsPublic] = useState(true);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (!currentUser) {
                navigate("/login");
            } else {
                setUser(currentUser);
                setLoading(true);


                const userRef = doc(db, "users", currentUser.uid);
                try {
                    const userSnap = await getDoc(userRef);
                    if (userSnap.exists()) {
                        setProfile(userSnap.data());
                    }
                } catch (error) {
                    console.error("Error fetching user data:", error);
                }

                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, [navigate]);


    const addQuestion = () => {
        setQuestions([
            ...questions,
            { type: "multiple-choice", question: "", options: ["", "", "", ""], answer: 0 }
        ]);
    };

    const updateQuestion = (index, field, value) => {
        let updatedQuestions = [...questions];
        if (field === "type") {
            updatedQuestions[index] = {
                ...updatedQuestions[index],
                type: value,
                options: value === "multiple-choice" ? ["", "", "", ""] : [],
                answer: value === "true-false" ? "true" : ""
            };
        } else {
            updatedQuestions[index][field] = value;
        }
        setQuestions(updatedQuestions);
    };

    const updateOption = (questionIndex, optionIndex, value) => {
        setQuestions((prevQuestions) => {
            const updatedQuestions = [...prevQuestions];
            updatedQuestions[questionIndex].options[optionIndex] = value;
            return updatedQuestions;
        });
    };


    const addFlashcard = () => {
        setFlashcards([
            ...flashcards,
            { front: "", back: "" }
        ]);
    };

    const updateFlashcard = (index, field, value) => {
        const updatedFlashcards = [...flashcards];
        updatedFlashcards[index][field] = value;
        setFlashcards(updatedFlashcards);
    };

    const removeItem = (index, type) => {
        if (type === "question") {
            setQuestions(questions.filter((_, i) => i !== index));
        } else {
            setFlashcards(flashcards.filter((_, i) => i !== index));
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!title || !description || (quizType === "standard" && questions.length === 0) || (quizType === "flashcards" && flashcards.length === 0)) {
            alert("Please fill out all required fields.");
            return;
        }

        setLoading(true);
        const user = auth.currentUser;

        if (!user) {
            alert("You must be logged in to create a quiz.");
            return;
        }
        // eslint-disable-next-line
        const formattedQuestions = questions.map(q => ({
            questions: q.question,
            type: q.type,
            options: q.type === "multiple-choice" ? q.options.filter(option => option.trim() !== "") : [],
            answer: q.answer
        }));

        const quizData = {
            title: title.trim(),
            description: description.trim(),
            quizType,
            authorId: user.uid,
            public: ispublic,
            createdAt: Timestamp.now(),
            questions: quizType === "standard"
                ? questions.map(q => ({
                    question: q.question.trim(),
                    type: q.type,
                    options: q.options.map(option => option.trim()),
                    answer: q.answer
                }))
                : [],
            flashcards: quizType === "flashcards"
                ? flashcards.map(f => ({
                    front: f.front.trim(),
                    back: f.back.trim()
                }))
                : [],
        };

        try {
            await addDoc(collection(db, "quizzes"), quizData);
            alert("Quiz created successfully!");
            navigate("/dashboard");
        } catch (err) {
            console.error("Error creating quiz", err);
            alert("Failed to create quiz. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Navbar />
            <div className="container" >
                <h2 className="title">Create a Quiz</h2>
                <div className="form-group">
                    <label>Title:</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                    />
                </div>

                <div className="form-group">
                    <label> Description:</label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        required
                    />
                </div>

                <div className="row-group">
                    <div className="form-group checkbox-group">
                        <label>Public:</label>
                        <input
                            type="checkbox"
                            checked={ispublic}
                            onChange={(e) => setIsPublic(e.target.checked)}
                        />
                    </div>

                    <div className="form-group quiz-type-inline">
                        <label htmlFor="quizType">Quiz Type:</label>
                        <select value={quizType} onChange={(e) => {
                            setQuizType(e.target.value);
                            setQuestions([]);
                            setFlashcards([]);
                        }}>
                            <option value="standard">Standard</option>
                            <option value="flashcards">Flashcards</option>
                        </select>
                    </div>
                </div>

                {/* Standard Quiz */}
                {quizType === "standard" && (
                    <div className="question-section">
                        <h3 className="section-title">Questions</h3>
                        {questions.map((q, index) => (
                            <div key={index} className="question-card">

                                <div className="row-group">
                                    <div className="form-group">
                                        <label>Type:</label>
                                        <select value={q.type} onChange={(e) => updateQuestion(index, "type", e.target.value)}>
                                            <option value="multiple-choice">Multiple Choice</option>
                                            <option value="true-false">True/False</option>
                                            <option value="open-ended">Open-Ended</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Question:</label>
                                        <input type="text" value={q.question} onChange={(e) => updateQuestion(index, "question",
                                            e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Multiple Choice */}
                                {q.type === "multiple-choice" && (
                                    <div className="form-group">
                                        <label>Options:</label>
                                        <div className="option-grid">
                                            {q.options.map((option, optionIndex) => (
                                                <input
                                                    key={optionIndex}
                                                    type="text"
                                                    value={option}
                                                    placeholder={`Option ${optionIndex + 1}`}
                                                    onChange={(e) => updateOption(index, optionIndex, e.target.value)}
                                                    required
                                                />
                                            ))}
                                        </div>

                                        <div className="form-group">
                                            <label>Answer:</label>
                                            <input
                                                type="text"
                                                value={q.answer}
                                                onChange={(e) => updateQuestion(index, "answer", e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* True/False */}
                                {q.type === "true-false" && (
                                    <div className="form-group">
                                        <label>Answer:</label>
                                        <select value={q.answer} onChange={(e) => updateQuestion(index, "answer", e.target.value)}>
                                            <option value="True">True</option>
                                            <option value="False">False</option>
                                        </select>
                                    </div>
                                )}

                                {/* Open-Ended */}
                                {q.type === "open-ended" && (
                                    <div className="form-group">
                                        <label>Answer:</label>
                                        <input
                                            type="text"
                                            value={q.answer}
                                            onChange={(e) => updateQuestion(index, "answer", e.target.value)}
                                            required
                                        />
                                    </div>
                                )}

                                <button className="remove-btn" onClick={() => removeItem(index, "question")}>Remove Question</button>
                            </div>
                        ))}
                        <button className="add-btn" onClick={addQuestion}>Add Question</button>
                    </div>
                )}

                {/* Flashcards */}
                {quizType === "flashcards" && (
                    <div className="flashcard-section">
                        <h3 className="section-title">Flashcards</h3>
                        {flashcards.map((card, index) => (
                            <div key={index} className="flashcard-card">
                                <div className="form-group">
                                    <label>Front:</label>
                                    <input
                                        type="text"
                                        value={card.front}
                                        onChange={(e) => updateFlashcard(index, "front", e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Back:</label>
                                    <input
                                        type="text"
                                        value={card.back}
                                        onChange={(e) => updateFlashcard(index, "back", e.target.value)}
                                        required
                                    />
                                </div>

                                <button className="remove-btn" onClick={() => removeItem(index, "flashcard")}>Remove Flashcard</button>
                            </div>
                        ))}
                        <button className="add-btn" onClick={addFlashcard}>Add Flashcard</button>
                    </div>
                )}

                <button className="submit-btn" onClick={handleSubmit} disabled={loading}>Create Quiz</button>
            </div>
        </>
    );
};

export default QuizCreation;





